import { useIsMobile } from "@/hooks/use-mobile";
import { toApiSort } from "@/lib/utils";
import { routes } from "@/routes/utils";
import {
  useDeleteCommunityMutation,
  useGetCommunities,
  useGetPermissionByUserId,
  useGetUsersList,
} from "@/services";
import { useQueryClient } from "@tanstack/react-query";
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "@/components/Toast";

const useEmployee = () => {
  const [globalFilter, setGlobalFilter] = useState("");
  const [pageNumber, setPageNumber] = useState<number>(0);
  const [pageSize, setPageSize] = useState<number>(10);
  const [sorting, setSorting] = useState([{ id: "userId", desc: true }]);
  const [openDialog, setOpenDialog] = useState<boolean>(false);
  const [deletedRow] = useState<any>(null);
  const [userId, setUserId] = useState<string>("");
  const [showAffiliationDialog, setShowAffiliationDialog] =
    useState<boolean>(false);
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const isMobile = useIsMobile();

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "smooth" });
  }, []);

  //get communities
  const { data: communitiesData, isLoading: isLoadingCommunities } =
    useGetCommunities({});

  const { data: userPermission, isLoading: isUserPermissionLoading } =
    useGetPermissionByUserId({
      userId,
      queryParams: {
        enabled: !!userId,
      },
    });

  const {
    data: employeeList,
    isLoading: isEmployeeLoading,
    refetch: refetchDoc,
    isRefetching: isEmployeeRefetching,
  } = useGetUsersList({
    data: {
      filters: [
        {
          field: "role",
          value: "ROLE_EMPLOYEE",
          operator: "EQUAL",
        },
      ],
      search: globalFilter,
      searchFields: ["name"],
      sort: toApiSort(sorting, { field: "userId", direction: "DESC" }),
      pageNumber,
      pageSize,
    },
  });

  useEffect(() => {
    if (globalFilter.length >= 2 || globalFilter.length == 0) {
      refetchDoc();
    }
  }, [pageNumber, pageSize, globalFilter, sorting]);

  const { mutate: deleteInfo, isPending: isDeletePending } =
    useDeleteCommunityMutation({
      onError: (error?: any) => {
        toast.error(
          typeof error?.response?.data?.message === "string"
            ? error?.response?.data?.message
            : "Something went wrong"
        );
        return error?.response?.data?.message;
      },
      onSuccess: () => {
        queryClient.invalidateQueries({
          queryKey: ["communities"],
        });
        navigate(`/${routes.admin}/${routes.communities}`);
        toast.success("Community Deleted Successfully");
      },
    });

  // Define Table Columns
  const columns = [
    {
      accessorKey: "userId",
      header: "ID",
      accessorFn: (row: any) => String(row.userId),
      size: 100,
    },
    {
      accessorKey: "name",
      header: "Name",
      cell: ({ row }: any) => {
        return row.original.name ? (
          <div
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
            }}
            className="text-primary group hover:text-blue-800"
          >
            <span
              className="border-b-1 border-primary group-hover:border-blue-700 cursor-pointer"
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                navigate(`${row.original.userId}`);
              }}
            >
              {String(row.original.name)}
            </span>
          </div>
        ) : (
          <div className="px-3">{""}</div>
        );
      },
      size: 200,
    },
    {
      accessorKey: "phoneNumber",
      header: <div className=" w-full">Contact Phone</div>,
      accessorFn: (row: any) => String(row?.phoneNumber || ""),
    },
    {
      accessorKey: "email",
      header: <div className=" w-full">Email ID</div>,
      accessorFn: (row: any) => String(row?.email || ""),
    },
    {
      accessorKey: "city",
      header: "City",
      accessorFn: (row: any) => String(row?.city || ""),
    },
    {
      accessorKey: "state",
      header: "State",
      accessorFn: (row: any) => String(row?.state || ""),
    },
    {
      accessorKey: "communityAffiliation",
      header: "Community Affiliation",
      cell: ({ row }: any) => {
        return row.original.communityAffiliation ? (
          <div
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              setUserId(row.original.userId);
              setTimeout(() => {
                setShowAffiliationDialog(true);
              }, 200);
            }}
            className="text-primary group hover:text-blue-800 cursor-pointer px-3"
          >
            <span className="border-b-1 border-primary group-hover:border-blue-800">
              {String(row.original.communityAffiliation)}
            </span>
          </div>
        ) : (
          <div className="px-3">{""}</div>
        );
      },
    },
    {
      accessorKey: "dob",
      header: "DOB",
      accessorFn: (row: any) => String(row?.dob || ""),
    },
  ];

  const onClose = () => {
    setOpenDialog(false);
  };

  const onConfirm = (reason: string) => {
    deleteInfo({ id: deletedRow?.userId, reason });
    onClose();
  };

  const onAddEmployee = () => {
    navigate(`${routes.add}`);
  };

  return {
    onAddEmployee,
    globalFilter,
    setGlobalFilter,
    columns,
    communitiesData,
    isLoadingCommunities,
    onClose,
    openDialog,
    isDeletePending,
    onConfirm,
    employeeList,
    isEmployeeLoading,
    deletedRow,
    isMobile,
    setPageNumber,
    setPageSize,
    pageNumber,
    pageSize,
    sorting,
    setSorting,
    isEmployeeRefetching,
    showAffiliationDialog,
    setShowAffiliationDialog,
    userPermission,
    isUserPermissionLoading,
  };
};

export default useEmployee;
